import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import type { Store } from '@/types'
import { useWorkspace } from '@/contexts/WorkspaceContext'

const SELECTED_STORE_KEY = 'reflow_selected_store'

// Default fallback store if nothing is selected and no workspace default
const DEFAULT_STORE: Store = {
    storeId: 12847,
    name: 'Indiranagar',
    address: 'Indiranagar, Bangalore',
    latitude: 12.9716,
    longitude: 77.5946,
    cityId: 1,
    cityName: 'Bangalore'
}

interface StoreContextValue {
    selectedStore: Store
    setSelectedStore: (store: Store) => void
}

const StoreContext = createContext<StoreContextValue | undefined>(undefined)

export function StoreProvider({ children }: { children: ReactNode }) {
    const { currentWorkspace } = useWorkspace()

    const getInitialStore = (): Store => {
        // First try localStorage (user's explicit selection)
        const stored = localStorage.getItem(SELECTED_STORE_KEY)
        if (stored) {
            try {
                return JSON.parse(stored)
            } catch (e) {
                console.error('Failed to parse selected store', e)
            }
        }
        // Then try workspace default location
        if (currentWorkspace?.defaultLocation) {
            return currentWorkspace.defaultLocation
        }
        // Final fallback
        return DEFAULT_STORE
    }

    const [selectedStore, setSelectedStoreState] = useState<Store>(getInitialStore)

    // When workspace changes and user hasn't manually selected a store,
    // update to the workspace's default location
    useEffect(() => {
        const hasManualSelection = localStorage.getItem(SELECTED_STORE_KEY)
        if (!hasManualSelection && currentWorkspace?.defaultLocation) {
            setSelectedStoreState(currentWorkspace.defaultLocation)
        }
    }, [currentWorkspace?.id, currentWorkspace?.defaultLocation])

    const setSelectedStore = (store: Store) => {
        setSelectedStoreState(store)
        localStorage.setItem(SELECTED_STORE_KEY, JSON.stringify(store))
    }

    return (
        <StoreContext.Provider value={{ selectedStore, setSelectedStore }}>
            {children}
        </StoreContext.Provider>
    )
}

export function useStore() {
    const context = useContext(StoreContext)
    if (!context) {
        throw new Error('useStore must be used within a StoreProvider')
    }
    return context
}
